import type { Corpus, KeywordSearchGroup, KeywordSearchRow, SearchRegion } from '../types';
import { REGION_CATEGORY_MAP } from '../constants/filterConstants';

/** The terms a keyword search names: row keywords plus each proximity as
 *  ``a NEAR/n b`` — a proximity-only group has no rows. Used wherever a
 *  search is described in words (chat attach, history, Compare). */
export function keywordSearchTerms(groups: readonly KeywordSearchGroup[]): string[] {
  return groups.flatMap((g) => [
    ...(g.rows ?? []).map((r) => (r?.keyword ?? '').trim()),
    g.proximity ? `${g.proximity.term_a} NEAR/${g.proximity.max_distance} ${g.proximity.term_b}` : '',
  ]).filter(Boolean);
}

/** The words a proximity block asks for, for highlighting. */
export function proximityOperands(groups: readonly KeywordSearchGroup[]): string[] {
  return groups.flatMap((g) => (g.proximity ? [g.proximity.term_a, g.proximity.term_b] : []));
}

/**
 * Display-only pseudo-syntax for the QUERY line. Built from the groups
 * ``handleSearch`` sends plus the region / category filters; never parsed back.
 * Returns null when no group has a keyword or a proximity.
 */
export function formatKeywordQuery(
  groups: KeywordSearchGroup[],
  opts: {
    corpus: Corpus;
    jpJa: boolean;
    regions: readonly SearchRegion[];
    marketingCategories: readonly string[];
  },
): string | null {
  const term = (r: KeywordSearchRow): string => {
    const kw = r.keyword.trim();
    const field = r.drug_field ? r.drug_field
      : opts.corpus === 'fda_letters' ? 'letter'
      : opts.jpJa ? 'jp'
      : r.section_scope ? `label[${r.section_scope}]`
      : 'label';
    return `${r.negate ? 'NOT ' : ''}${field}:${r.drug_field && /\*|\+$/.test(kw) ? kw : `"${kw}"`}`;
  };

  const active = groups
    .map((g) => ({ g, rows: g.rows.filter((r) => r.keyword.trim().length > 0) }))
    .filter((x) => x.rows.length > 0 || x.g.proximity);
  if (active.length === 0) return null;

  const parts = active.map(({ g, rows }, i) => {
    const op = g.inner_operator;
    let s = rows.map(term).join(` ${op} `);
    if (g.proximity) {
      const near = `NEAR/${g.proximity.max_distance}("${g.proximity.term_a}", "${g.proximity.term_b}")`;
      s = s ? `${s} AND ${near}` : near;
    }
    const wrap = active.length > 1 && (rows.length > 1 || (g.proximity && rows.length > 0));
    return { text: wrap ? `(${s})` : s, op: i === 0 ? '' : ` ${g.preceding_operator ?? 'AND'} ` };
  });
  let out = parts.map((p) => p.op + p.text).join('');

  if (opts.corpus === 'labels' && !opts.jpJa) {
    if (opts.regions.length > 0) {
      out += opts.regions.length === 1
        ? ` AND region:${opts.regions[0]}`
        : ` AND region:(${opts.regions.join(' OR ')})`;
    }
    const allowed = new Set(opts.regions.flatMap((r) => REGION_CATEGORY_MAP[r].map((c) => c.key)));
    const cats = opts.marketingCategories.filter((k) => allowed.has(k)).map((k) => k.toUpperCase());
    if (cats.length > 0) {
      out += cats.length === 1 ? ` AND app:${cats[0]}` : ` AND app:(${cats.join(' OR ')})`;
    }
  }
  return out;
}

/** The QUERY line for a settled keyword run, as reported to the chat shell.
 *  ``jpJa`` follows the request (the backend searches the Japanese original
 *  when ``sources`` includes ``jp_ja``); it swaps the field prefix to ``jp:``
 *  and drops the region / app clauses, same as the form's QUERY line. */
export function keywordRunQueryLine(
  groups: KeywordSearchGroup[],
  filters: { searchMode?: string; regions: readonly SearchRegion[]; marketingCategories: readonly string[] },
  request: { sources?: readonly string[] },
): string | undefined {
  return formatKeywordQuery(groups, {
    corpus: filters.searchMode === 'fda_letters' ? 'fda_letters' : 'labels',
    jpJa: request.sources?.includes('jp_ja') ?? false,
    regions: filters.regions,
    marketingCategories: filters.marketingCategories,
  }) ?? undefined;
}
